/**
 * BuilderDocumentPreview
 * Full document stack — pages rendered as clean printed sheets.
 * No builder chrome inside the pages themselves.
 */
import { FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import type {
  ProposalDocumentLayout,
  ProposalDocumentLayoutPage,
  TemplatePageType,
} from "../../types";
import { BuilderPagePreview } from "./BuilderPagePreview";
import type { ProposalCanvasContext } from "./ProposalBuilderCanvas";

interface BuilderDocumentPreviewProps {
  layout: ProposalDocumentLayout | null | undefined;
  className?: string;
  proposalContext?: ProposalCanvasContext;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function pageTypeLabel(type?: string) {
  switch (type as TemplatePageType) {
    case "cover":        return "Cover Page";
    case "cover_letter": return "Cover Letter";
    case "line_items":   return "Services & Pricing";
    case "static_page":  return "Static Page";
    case "custom":       return "Custom Page";
    case "closing":      return "Closing Page";
    case "signature":    return "Signature Page";
    default:             return "Page";
  }
}

function pageTypeChipClass(type?: string) {
  switch (type as TemplatePageType) {
    case "cover":        return "border-sky-400/30 bg-sky-400/10 text-sky-700";
    case "cover_letter": return "border-violet-400/30 bg-violet-400/10 text-violet-700";
    case "line_items":   return "border-emerald-400/30 bg-emerald-400/10 text-emerald-700";
    case "static_page":  return "border-amber-400/30 bg-amber-400/10 text-amber-700";
    case "closing":      return "border-pink-400/30 bg-pink-400/10 text-pink-700";
    case "signature":    return "border-cyan-400/30 bg-cyan-400/10 text-cyan-700";
    default:             return "border-black/10 bg-black/[0.04] text-black/55";
  }
}

function normalizePages(layout: ProposalDocumentLayout | null | undefined): ProposalDocumentLayoutPage[] {
  const pages = Array.isArray(layout?.pages) ? layout!.pages : [];

  return pages
    .map((page, index) => ({
      ...page,
      id:    page.id ?? `page_${index}`,
      title: typeof page.title === "string" && page.title.trim().length > 0
        ? page.title
        : `${pageTypeLabel(page.type)} ${index + 1}`,
      type:     page.type ?? "custom",
      order:    typeof page.order === "number" ? page.order : index,
      settings: page.settings && typeof page.settings === "object" ? page.settings : {},
      sections: Array.isArray(page.sections) ? page.sections : [],
    }))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

function getTotalSections(pages: ProposalDocumentLayoutPage[]) {
  return pages.reduce(
    (sum, p) => sum + (Array.isArray(p.sections) ? p.sections.length : 0),
    0,
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BuilderDocumentPreview({ layout, className, proposalContext }: BuilderDocumentPreviewProps) {
  const pages         = normalizePages(layout);
  const totalSections = getTotalSections(pages);

  return (
    <div className={cn("flex flex-col", className)}>
      {/* Preview header */}
      <div className="mb-4 flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/35">
            Document Preview
          </p>
          <p className="mt-1 text-sm font-medium text-white/75">
            {pages.length} page{pages.length === 1 ? "" : "s"} · {totalSections} section{totalSections === 1 ? "" : "s"}
          </p>
        </div>
        <div className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/35">
          Read-only
        </div>
      </div>

      {/* Print tray — dark background, pages centered at letter width */}
      <div className="rounded-2xl bg-[#1a1b1e] py-10 px-6">
        {pages.length === 0 ? (
          <div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04]">
              <FileText className="h-5 w-5 text-white/30" />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-white/55">No pages yet</p>
              <p className="mt-1 text-xs text-white/30">
                Add pages in the Canvas tab to see a full preview here.
              </p>
            </div>
          </div>
        ) : (
          <div className="mx-auto w-full max-w-[816px] space-y-10">
            {pages.map((page, index) => (
              <div key={page.id} className="space-y-2.5">
                {/* Page label */}
                <div className="flex items-center gap-2.5 px-1">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-white/10 text-[9px] font-bold text-white/40">
                    {index + 1}
                  </span>
                  <span className="truncate text-[11px] font-medium text-white/50">
                    {page.title || `Page ${index + 1}`}
                  </span>
                  <span
                    className={cn(
                      "shrink-0 rounded-full border px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.1em]",
                      pageTypeChipClass(page.type),
                    )}
                  >
                    {pageTypeLabel(page.type)}
                  </span>
                </div>

                {/* Page at letter-size — shadow makes it look like paper */}
                <div className="shadow-[0_4px_32px_rgba(0,0,0,0.45)]">
                  <BuilderPagePreview page={page} proposalContext={proposalContext} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
